if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupMainCarousel();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupMainCarousel() {
        // Only top-level slides belong in the carousel. The image-open buttons
        // also carry data-media-id, and treating those nested controls as slides
        // created a blank step after every real product image.
        this.carouselItems = Array.from(
          this.elements.viewer?.querySelectorAll('.product__media-list > .product__media-item') || []
        );
        if (this.carouselItems.length < 2) return;

        this.carouselCurrent = Math.max(
          0,
          this.carouselItems.findIndex((item) => item.classList.contains('is-active'))
        );
        this.carouselPrevious = this.elements.viewer.querySelector('.slider-button--prev');
        this.carouselNext = this.elements.viewer.querySelector('.slider-button--next');
        this.carouselDots = Array.from(this.elements.viewer.querySelectorAll('.mobile-gallery-dot'));
        this.carouselScroller = this.elements.viewer.querySelector('.product__media-list');

        const move = (step, event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showCarouselItem(this.carouselCurrent + step);
        };

        this.carouselPrevious?.addEventListener('click', move.bind(this, -1), true);
        this.carouselNext?.addEventListener('click', move.bind(this, 1), true);

        // On phones, update the indicator during the gesture instead of waiting
        // for Dawn's debounced slideChanged event after scroll snapping settles.
        this.mobileScrollLeft = this.carouselScroller?.scrollLeft || 0;
        this.carouselScroller?.addEventListener(
          'scroll',
          () => {
            if (this.mql.matches) return;
            const scrollLeft = this.carouselScroller.scrollLeft;
            const slideStep =
              this.carouselItems[1]?.offsetLeft - this.carouselItems[0]?.offsetLeft ||
              this.carouselScroller.clientWidth ||
              1;
            const position = scrollLeft / slideStep;
            const movingForward = scrollLeft >= this.mobileScrollLeft;
            const nextIndex = movingForward
              ? Math.ceil(position - 0.002)
              : Math.floor(position + 0.002);
            this.mobileScrollLeft = scrollLeft;
            this.updateMobileCarouselIndicator(nextIndex);
          },
          { passive: true }
        );

        this.showCarouselItem(this.carouselCurrent);
      }

      updateMobileCarouselIndicator(index) {
        if (!this.carouselItems?.length) return;
        const boundedIndex = Math.max(0, Math.min(this.carouselItems.length - 1, index));
        if (boundedIndex === this.carouselCurrent) return;
        this.carouselCurrent = boundedIndex;
        this.carouselItems.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === boundedIndex));
        this.carouselDots?.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === boundedIndex));
      }

      showCarouselItem(index) {
        if (!this.carouselItems?.length) return;
        this.carouselCurrent =
          ((index % this.carouselItems.length) + this.carouselItems.length) % this.carouselItems.length;

        const mobileSwipeLayout = !this.mql.matches;
        this.carouselItems.forEach((item, itemIndex) => {
          const active = itemIndex === this.carouselCurrent;
          item.classList.toggle('is-active', active);
          item.hidden = mobileSwipeLayout ? false : !active;
          item.setAttribute('aria-hidden', mobileSwipeLayout || active ? 'false' : 'true');
        });

        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (current) current.textContent = String(this.carouselCurrent + 1);
        if (total) total.textContent = String(this.carouselItems.length);
        if (this.carouselPrevious) this.carouselPrevious.disabled = false;
        if (this.carouselNext) this.carouselNext.disabled = false;
        this.carouselDots?.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === this.carouselCurrent));

        // These product galleries contain ordinary images. Avoid invoking the
        // media player and thumbnail slider during initialization; doing so can
        // recursively wake Shopify's slider observers before they are ready.
      }

      onSlideChanged(event) {
        const carouselIndex = this.carouselItems?.indexOf(event.detail.currentElement);
        if (carouselIndex >= 0 && !this.mql.matches) {
          this.carouselCurrent = carouselIndex;
          this.carouselItems.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === carouselIndex));
          this.carouselDots?.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === carouselIndex));
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const carouselIndex = this.carouselItems?.indexOf(activeMedia);
        if (carouselIndex >= 0) this.showCarouselItem(carouselIndex);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
